/**
 * Public Proposals API
 * ====================
 * API functions for unauthenticated client proposal review via token.
 * Uses raw axios (no auth interceptors needed).
 */

import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const publicClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

/**
 * Verify client email before showing proposal
 */
export const verifyProposalEmail = async (token, data) => {
  const response = await publicClient.post(`/api/v1/public/proposals/${token}/verify-email`, data);
  return response.data;
};

/**
 * Get proposal for client review
 */
export const getClientProposal = async (token) => {
  const response = await publicClient.get(`/api/v1/public/proposals/${token}`);
  return response.data;
};

/**
 * Get per-org branding for the access gate (before email verification).
 * Returns only branding (colors + logo URLs), never proposal content.
 */
export const getProposalBranding = async (token) => {
  const response = await publicClient.get(`/api/v1/public/proposals/${token}/branding`);
  return response.data;
};

/**
 * Add a client comment on a proposal section
 */
export const addClientComment = async (token, data) => {
  const response = await publicClient.post(`/api/v1/public/proposals/${token}/comments`, data);
  return response.data;
};

/**
 * Delete a client comment
 */
export const deleteClientComment = async (token, commentId) => {
  const response = await publicClient.delete(`/api/v1/public/proposals/${token}/comments/${commentId}`);
  return response.data;
};

/**
 * Client approves the proposal
 */
export const approveProposal = async (token, data) => {
  const response = await publicClient.post(`/api/v1/public/proposals/${token}/approve`, data);
  return response.data;
};

/**
 * Client rejects the proposal
 */
export const rejectProposal = async (token, data) => {
  const response = await publicClient.post(`/api/v1/public/proposals/${token}/reject`, data);
  return response.data;
};

/**
 * Stream a chat message response from the proposal AI assistant via SSE.
 * @param {string} token - Proposal token
 * @param {string} sessionId - Chat session ID
 * @param {string} message - User message
 * @param {object} callbacks - { onToken, onDone, onError, signal }
 */
export const streamProposalChatMessage = async (token, sessionId, message, { onToken, onDone, onError, signal }) => {
  const url = `${API_BASE_URL}/api/v1/public/proposals/${token}/chat`;

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'text/event-stream' },
      body: JSON.stringify({ session_id: sessionId, message }),
      signal,
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n\n');
      buffer = lines.pop();

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        try {
          const payload = JSON.parse(line.slice(6));
          if (payload.done) {
            onDone?.(payload);
          } else if (payload.error) {
            onError?.(new Error(payload.error));
          } else if (payload.token !== undefined) {
            onToken?.(payload.token);
          }
        } catch {
          // Skip malformed SSE lines
        }
      }
    }
  } catch (err) {
    onError?.(err);
  }
};

/**
 * Get chat history for a proposal chat session
 */
export const getProposalChatHistory = async (token, sessionId, limit = 50) => {
  const response = await publicClient.get(`/api/v1/public/proposals/${token}/chat/history`, {
    params: { session_id: sessionId, limit },
  });
  return response.data;
};

/**
 * Request a LiveKit connection token for avatar presentation mode
 */
export const requestLiveKitConnect = async (token) => {
  const response = await publicClient.post(`/api/v1/public/proposals/${token}/avatar/livekit-connect`);
  return response.data;
};

/**
 * Request a LiveKit connection token for audio-only v1 presentation mode
 */
export const requestLiveKitConnectV1 = async (token) => {
  const response = await publicClient.post(`/api/v1/public/proposals/${token}/avatar/livekit-connect-v1`);
  return response.data;
};

/**
 * Request a LiveKit connection token for v3 presentation mode (conversational + LemonSlice avatar)
 */
export const requestLiveKitConnectV3 = async (token) => {
  const response = await publicClient.post(`/api/v1/public/proposals/${token}/avatar/livekit-connect-v3`);
  return response.data;
};

/**
 * Request a LiveKit connection token for v4 presentation mode (presenter + Tavus avatar)
 */
export const requestLiveKitConnectV4 = async (token) => {
  const response = await publicClient.post(`/api/v1/public/proposals/${token}/avatar/livekit-connect-v4`);
  return response.data;
};

/**
 * Record avatar session end for cost tracking
 */
export const reportAvatarSessionEnd = async (token, sessionStartedAt, provider = 'tavus') => {
  const response = await publicClient.post(`/api/v1/public/proposals/${token}/avatar/session-end`, {
    session_started_at: sessionStartedAt,
    provider,
  });
  return response.data;
};
